import ErrorView from "@/components/ErrorView";
import Circular from "@/components/Circular";
import SelectedListItemBar from "@/features/list/components/SelectedListItemBar";
import DefaultLayout from "@/layouts/DefaultLayout";
import client from "@/utils/api/client";
import publicPath from "@/utils/publicPath";
import { notFoundWrapper } from "@/utils/notFoundWrapper";
import { ListItemKind, ListItemResponse, ListResponse } from "@/web-client/api";
import EditListItemContext from "@/contexts/EditListItemContext";
import StateContext from "@/contexts/StateContext";
import usePagination from "@/hooks/usePagination";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { NextSeo } from "next-seo";
import InfiniteScroll from "react-infinite-scroll-component";
import usePageView from "@/hooks/usePageView";
import Error from "../../_error";
import ListChildItem from "@/features/list/components/ListChildItem";
import { useMemo } from "react";
import ListProfile from "@/features/list/components/ListProfile";
import Link from "next/link";
import { useRouter } from "next/router";
import useFireRevalidateOnCloseModalShowDetail from "@/hooks/useFireRevalidateOnCloseModalShowDetail";
import FavoriteItemList from "@/components/FavoriteItemList";
import EditableListChildItemWrapper from "@/features/list/components/EditableListChildItemWrapper";
import useList from "@/features/list/hooks/useList";
import useEditListItems from "@/features/list/hooks/useEditListItems";

interface Props {
  id: number;
  initialList?: ListResponse;
}

const filterBtns = [
  {
    label: "すべて",
    kind: undefined,
  },
  {
    label: "プロジェクト",
    kind: ListItemKind.PROJECT,
  },
  {
    label: "写真",
    kind: ListItemKind.POST,
  },
  {
    label: "建材",
    kind: ListItemKind.BUILDING_MATERIAL,
  },
  {
    label: "家具",
    kind: ListItemKind.INTERIOR,
  },
];

const ListPostsPage: NextPage<Props> = ({ id, initialList }) => {
  usePageView(initialList?.maker_id);
  const { list, setList, error } = useList(id, initialList);
  const {
    query: { kind },
  } = useRouter();

  const numKind = useMemo(() => {
    if (kind === undefined) return undefined;

    return parseInt(kind as string, 10);
  }, [kind]);

  const ownerName = useMemo<string | undefined>(
    () => list?.maker?.name || list?.user?.screen_name,
    [list],
  );

  const { data, dataLength, next, hasNext, notFound, revalidate } =
    usePagination<ListItemResponse>(
      kind == undefined
        ? `/lists/${id}/items`
        : `/lists/${id}/items?kind=${kind}`,
      20,
      async ({ limit, cursor }) => {
        const { data } = await client.listsIdItemsGet({
          id,
          kind: numKind as ListItemKind,
          limit,
          cursor: cursor as number,
        });

        return data;
      },
      { waitForAuth: true },
    );
  const editListItems = useEditListItems(id);

  useFireRevalidateOnCloseModalShowDetail(revalidate);

  if (error) return <Error statusCode={404} label="ページが見つかりません" />;

  if (!list) return <div></div>;

  return (
    <DefaultLayout>
      <NextSeo
        title={`${ownerName}/${list.name}`}
        description={list.description || `${ownerName}のリスト「${list.name}」`}
        openGraph={{
          images: list.item_images.map((i) => {
            return { url: i.urls.original };
          }),
        }}
        canonical={publicPath(`/lists/${list.id}`)}
      />
      <StateContext.Provider value={{ update: setList }}>
        <EditListItemContext.Provider value={editListItems}>
          <main className="Container user space-y-80">
            <ListProfile list={list} />
            <div className="space-y-24">
              <ul className="[&>*]:inline-block space-x-4 laptop:text-center whitespace-nowrap overflow-x-scroll hidden-scrollbar px-12">
                {filterBtns.map(({ label, kind }) => {
                  const selected = numKind === kind;
                  const href =
                    kind !== undefined
                      ? `/lists/${list.id}?kind=${kind}`
                      : `/lists/${list.id}`;

                  return (
                    <li key={label}>
                      <Link
                        href={href}
                        prefetch={true}
                        replace={true}
                        className={[
                          "block font-bold rounded-full p-6 px-12 laptop:py-6 laptop:px-12  transition-all duration-300",
                          "hover:bg-black hover:text-white hover:font-bold",
                          selected ? "bg-black text-white" : "",
                        ].join(" ")}
                      >
                        {label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <div className="px-16">
                <InfiniteScroll
                  style={{ overflow: "visible" }}
                  dataLength={dataLength}
                  hasMore={hasNext}
                  next={next}
                  loader={<Circular />}
                >
                  <FavoriteItemList
                    data={data}
                    onRender={(item) => (
                      <EditableListChildItemWrapper
                        key={item.id}
                        listItem={item as ListItemResponse}
                      >
                        <ListChildItem listItem={item} />
                      </EditableListChildItemWrapper>
                    )}
                  />
                </InfiniteScroll>

                {notFound && (
                  <ErrorView>登録されたアイテムはありません</ErrorView>
                )}
              </div>
            </div>

            {editListItems.isEditing && <SelectedListItemBar />}
          </main>
        </EditListItemContext.Provider>
      </StateContext.Provider>
    </DefaultLayout>
  );
};

export default ListPostsPage;

export const getStaticProps: GetStaticProps = async ({ params }) => {
  return notFoundWrapper<Props>(async () => {
    const id = params.id as string;
    const props: Props = { id: Number(id) };
    try {
      const response = await client.listsIdGet({ id: Number(id) });
      props.initialList = response.data;
    } catch (error) {
      if (error.response.status !== 403) {
        throw error;
      }
    }

    return {
      props,
      revalidate: 1,
    };
  });
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: "blocking",
});
